import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  serverTimestamp,
  FieldPath,
  onSnapshot,
  writeBatch
} from 'firebase/firestore'
import { db } from '../services/firebase/config'
import { targetedCloneForMutation } from '@/utils/cloneHelpers'
import { useUserStore } from './userStore'
import MediaService from '../services/media/MediaService'
import { v4 as uuidv4 } from 'uuid'
import {
  normalizePlayers,
  getPlayerCount,
  hasPlayer,
  getPlayerRole,
  DEFAULT_PLAYER_ROLE
} from '@/utils/campaignPlayers'
import {
  buildCharacterCampaignPreview,
  resolveCharacterCampaignIds
} from '@/utils/characterCampaignPreview'
import {
  buildCampaignCharacterPreviewDoc,
  getCampaignCharacterPreviewDocRef
} from '@/services/campaign/CampaignCharacterPreviewService'

const toCharacterMap = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return { ...value }
  }
  if (Array.isArray(value)) {
    return value.reduce((acc, id) => {
      if (!id) return acc
      acc[id] = { id }
      return acc
    }, {})
  }
  return {}
}

const PUBLIC_CAMPAIGNS_LIST_CACHE_KEY = 'public_campaigns_discover_list_v1'
const PUBLIC_CAMPAIGNS_PAGE_CACHE_PREFIX = 'public_campaigns_discover_page_v1'
const PUBLIC_CAMPAIGNS_CACHE_TTL_MS = 5 * 60 * 1000

let publicCampaignsListMemoryCache = null
let publicCampaignsPageMemoryCache = new Map()
let publicCampaignsListPendingRequest = null
let publicCampaignsPagePendingRequests = new Map()

const normalizePublicCampaignRecord = (record) => {
  const cloned = targetedCloneForMutation(record || {})
  return {
    ...cloned,
    players: normalizePlayers(cloned.players)
  }
}

const clonePublicCampaignList = (list) =>
  (Array.isArray(list) ? list : []).map((campaign) => normalizePublicCampaignRecord(campaign))

const isValidPublicCampaignCacheEntry = (
  entry,
  maxAgeMs = PUBLIC_CAMPAIGNS_CACHE_TTL_MS
) => {
  if (!entry || typeof entry.ts !== 'number') return false
  return Date.now() - entry.ts <= maxAgeMs
}

const canUseSessionStorage = () => {
  try {
    return typeof sessionStorage !== 'undefined'
  } catch {
    return false
  }
}

const getPublicCampaignPageCacheKey = ({
  page = 1,
  pageSize = 25,
  sortBy = 'created',
  descending = true
} = {}) =>
  `${PUBLIC_CAMPAIGNS_PAGE_CACHE_PREFIX}:${page}:${pageSize}:${sortBy}:${descending ? 'desc' : 'asc'}`

const readPublicCampaignListCacheEntry = ({
  maxAgeMs = PUBLIC_CAMPAIGNS_CACHE_TTL_MS,
  allowExpired = false
} = {}) => {
  const isUsable = (entry) =>
    !!entry &&
    typeof entry.ts === 'number' &&
    Array.isArray(entry.data) &&
    (allowExpired || isValidPublicCampaignCacheEntry(entry, maxAgeMs))

  if (isUsable(publicCampaignsListMemoryCache)) {
    return {
      ts: publicCampaignsListMemoryCache.ts,
      data: clonePublicCampaignList(publicCampaignsListMemoryCache.data)
    }
  }

  if (!canUseSessionStorage()) return null

  try {
    const raw = sessionStorage.getItem(PUBLIC_CAMPAIGNS_LIST_CACHE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw)
    if (!parsed || !Array.isArray(parsed.data)) {
      sessionStorage.removeItem(PUBLIC_CAMPAIGNS_LIST_CACHE_KEY)
      return null
    }

    if (!allowExpired && !isValidPublicCampaignCacheEntry(parsed, maxAgeMs)) {
      sessionStorage.removeItem(PUBLIC_CAMPAIGNS_LIST_CACHE_KEY)
      return null
    }

    publicCampaignsListMemoryCache = {
      ts: parsed.ts,
      data: clonePublicCampaignList(parsed.data)
    }

    return {
      ts: publicCampaignsListMemoryCache.ts,
      data: clonePublicCampaignList(publicCampaignsListMemoryCache.data)
    }
  } catch {
    try {
      sessionStorage.removeItem(PUBLIC_CAMPAIGNS_LIST_CACHE_KEY)
    } catch {
      /* ignore cache cleanup failures */
    }
    return null
  }
}

const writePublicCampaignListCache = (campaigns) => {
  const entry = {
    ts: Date.now(),
    data: clonePublicCampaignList(campaigns)
  }

  publicCampaignsListMemoryCache = entry

  if (canUseSessionStorage()) {
    try {
      sessionStorage.setItem(PUBLIC_CAMPAIGNS_LIST_CACHE_KEY, JSON.stringify(entry))
    } catch {
      /* ignore cache persistence failures */
    }
  }

  return clonePublicCampaignList(entry.data)
}

const readPublicCampaignPageCacheEntry = (
  key,
  { maxAgeMs = PUBLIC_CAMPAIGNS_CACHE_TTL_MS, allowExpired = false } = {}
) => {
  const normalizeEntry = (entry) => {
    if (!entry || typeof entry.ts !== 'number' || !entry.data) return null
    if (!allowExpired && !isValidPublicCampaignCacheEntry(entry, maxAgeMs)) return null

    const campaigns = clonePublicCampaignList(entry.data.campaigns)
    return {
      ts: entry.ts,
      data: {
        campaigns,
        lastDoc: entry.data.lastDoc ? { id: entry.data.lastDoc.id } : null,
        total: Number(entry.data.total) || 0
      }
    }
  }

  const inMemory = normalizeEntry(publicCampaignsPageMemoryCache.get(key))
  if (inMemory) return inMemory

  if (!canUseSessionStorage()) return null

  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null

    const parsed = JSON.parse(raw)
    const normalized = normalizeEntry(parsed)
    if (!normalized) {
      sessionStorage.removeItem(key)
      return null
    }

      publicCampaignsPageMemoryCache.set(key, {
        ts: normalized.ts,
        data: {
          campaigns: clonePublicCampaignList(normalized.data.campaigns),
          lastDoc: normalized.data.lastDoc,
          total: normalized.data.total
        }
      })

    return {
      ts: normalized.ts,
      data: {
        campaigns: clonePublicCampaignList(normalized.data.campaigns),
        lastDoc: normalized.data.lastDoc ? { id: normalized.data.lastDoc.id } : null,
        total: normalized.data.total
      }
    }
  } catch {
    try {
      sessionStorage.removeItem(key)
    } catch {
      /* ignore cache cleanup failures */
    }
    return null
  }
}

const writePublicCampaignPageCache = (key, result) => {
  const entry = {
    ts: Date.now(),
    data: {
      campaigns: clonePublicCampaignList(result?.campaigns),
      lastDoc: result?.lastDoc?.id ? { id: result.lastDoc.id } : null,
      total: Number(result?.total) || 0
    }
  }

  publicCampaignsPageMemoryCache.set(key, entry)

  if (canUseSessionStorage()) {
    try {
      sessionStorage.setItem(key, JSON.stringify(entry))
    } catch {
      /* ignore cache persistence failures */
    }
  }

  return {
    campaigns: clonePublicCampaignList(entry.data.campaigns),
    lastDoc: result?.lastDoc || entry.data.lastDoc,
    total: entry.data.total
  }
}

const clearPublicCampaignCachesInternal = () => {
  publicCampaignsListMemoryCache = null
  publicCampaignsPageMemoryCache = new Map()
  publicCampaignsListPendingRequest = null
  publicCampaignsPagePendingRequests = new Map()

  if (!canUseSessionStorage()) return

  try {
    sessionStorage.removeItem(PUBLIC_CAMPAIGNS_LIST_CACHE_KEY)
    const keysToRemove = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (typeof key === 'string' && key.startsWith(PUBLIC_CAMPAIGNS_PAGE_CACHE_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key))
  } catch {
    /* ignore cache cleanup failures */
  }
}

export const useCampaignStore = defineStore('campaign', () => {
  const campaigns = ref([])
  const isLoading = ref(false)
  const error = ref(null)
  const currentCampaign = ref(null)
  const campaignCharacters = ref([])
  const campaignUnsubscribe = ref(null)
  const userStore = useUserStore()

  // Getters
  const getCampaigns = computed(() => campaigns.value)
  const getUserCampaigns = computed(() => {
    const userId = userStore.userId
    if (!userId) return []

    return campaigns.value.filter(
      (campaign) => campaign.dm === userId || hasPlayer(campaign.players, userId)
    )
  })

  const getDMCampaigns = computed(() => {
    const userId = userStore.userId
    if (!userId) return []

    return campaigns.value.filter((campaign) => campaign.dm === userId)
  })

  const getPlayerCampaigns = computed(() => {
    const userId = userStore.userId
    if (!userId) return []
    return campaigns.value.filter((campaign) => {
      if (campaign.dm === userId) return false
      return hasPlayer(campaign.players, userId)
    })
  })

  const getCurrentCampaign = computed(() => currentCampaign.value)
  const getCampaignCharacters = computed(() => campaignCharacters.value)

  // Actions
  async function fetchPlayerCampaigns() {
    if (!userStore.isAuthenticated) {
      return []
    }

    isLoading.value = true
    error.value = null

    try {
      const userId = userStore.userId
      const roleFieldPath = new FieldPath('players', userId, 'role')
      const playerSnapshot = await getDocs(
        query(collection(db, 'campaigns'), where(roleFieldPath, 'in', ['player', 'moderator']))
      )

      const playerCampaigns = playerSnapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          ...data,
          players: normalizePlayers(data.players)
        }
      })

      const existingDMCampaigns = campaigns.value
        .filter((campaign) => campaign.dm === userId)
        .map((campaign) =>
          targetedCloneForMutation({
            ...campaign,
            players: normalizePlayers(campaign.players)
          })
        )

      const uniquePlayerCampaigns = playerCampaigns
        .filter((campaign) => campaign.dm !== userId)
        .map((campaign) => targetedCloneForMutation(campaign))

      campaigns.value = [...existingDMCampaigns, ...uniquePlayerCampaigns]

      return uniquePlayerCampaigns.map((campaign) => ({ ...campaign }))
    } catch (err) {
      console.error('Error fetching player campaigns:', err)
      error.value = err.message
      return []
    } finally {
      isLoading.value = false
    }
  }

  async function fetchDMCampaigns() {
    if (!userStore.isAuthenticated) {
      return []
    }

    isLoading.value = true
    error.value = null

    try {
      const userId = userStore.userId

      // Fetch only campaigns where user is DM
      const dmQuery = query(collection(db, 'campaigns'), where('dm', '==', userId))
      const dmSnapshot = await getDocs(dmQuery)

      const dmCampaigns = dmSnapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          players: normalizePlayers(data.players)
        }
      })

      // Update the campaigns store with these campaigns
      // We'll merge with existing player campaigns if they exist
      const existingPlayerCampaigns = campaigns.value
        .filter((campaign) => campaign.dm !== userId)
        .map((campaign) =>
          targetedCloneForMutation({
            ...campaign,
            players: normalizePlayers(campaign.players)
          })
        )

      campaigns.value = [
        ...dmCampaigns.map((it) => targetedCloneForMutation(it)),
        ...existingPlayerCampaigns
      ]

      return dmCampaigns
    } catch (err) {
      console.error('Error fetching DM campaigns:', err)
      error.value = err.message
      return []
    } finally {
      isLoading.value = false
    }
  }

  async function fetchCampaigns() {
    if (!userStore.isAuthenticated) {
      return []
    }

    isLoading.value = true
    error.value = null

    try {
      const userId = userStore.userId

      // Fetch campaigns where user is DM
      const campaignsRef = collection(db, 'campaigns')
      const dmQuery = query(campaignsRef, where('dm', '==', userId))
      const roleFieldPath = new FieldPath('players', userId, 'role')
      const playerQuery = query(campaignsRef, where(roleFieldPath, 'in', ['player', 'moderator']))

      const [dmSnapshot, playerSnapshot] = await Promise.all([
        getDocs(dmQuery),
        getDocs(playerQuery)
      ])

      // Convert to arrays of campaign data with IDs
      const mapCampaign = (docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          ...data,
          players: normalizePlayers(data.players)
        }
      }

      const dmCampaigns = dmSnapshot.docs.map(mapCampaign)
      const playerCampaigns = playerSnapshot.docs.map(mapCampaign)

      // Create a map to deduplicate by ID
      const campaignMap = new Map()

      // Add all DM campaigns first
      dmCampaigns.forEach((campaign) => {
        campaignMap.set(campaign.id, campaign)
      })

      // Add player campaigns that aren't already added as DM campaigns
      playerCampaigns.forEach((campaign) => {
        if (!campaignMap.has(campaign.id)) {
          campaignMap.set(campaign.id, campaign)
        }
      })

      // Convert map values back to array and clone per-item
      campaigns.value = Array.from(campaignMap.values()).map((it) => targetedCloneForMutation(it))

      return campaigns.value
    } catch (error) {
      console.error('Error fetching campaigns:', error)
      error.value = error.message
      return []
    } finally {
      isLoading.value = false
    }
  }

  async function fetchCampaign(campaignId, forceRefresh = false) {
    if (!forceRefresh && currentCampaign.value && currentCampaign.value.id === campaignId) {
      return currentCampaign.value
    }

    try {
      isLoading.value = true
      if (typeof userStore.waitForAuthState === 'function') {
        await userStore.waitForAuthState({ requireUser: false, timeoutMs: 8000 })
      }
      // Always get fresh data from Firestore
      const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId))

      if (campaignDoc.exists()) {
        const campaignData = campaignDoc.data()
        // Set the current campaign with fresh data
        currentCampaign.value = {
          id: campaignDoc.id,
          ...campaignData,
          players: normalizePlayers(campaignData.players)
        }
        return currentCampaign.value
      } else {
        console.error(`Campaign with ID ${campaignId} not found`)
        return null
      }
    } catch (error) {
      console.error('Error fetching campaign:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  async function createCampaign(campaignData) {
    if (!userStore.isAuthenticated) {
      throw new Error('You must be logged in to create a campaign')
    }

    isLoading.value = true
    error.value = null

    try {
      const userId = userStore.userId

      // Add required fields
      const campaign = {
        ...campaignData,
        dm: userId,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        players: normalizePlayers(campaignData.players),
        characters: campaignData.characters || {}
      }

      // Ensure settings object exists
      if (!campaign.settings) {
        campaign.settings = {
          isPublic: false,
          allowPlayerJoin: false
        }
      }

      const docRef = await addDoc(collection(db, 'campaigns'), campaign)

      const newCampaign = {
        id: docRef.id,
        ...campaign,
        created: new Date().toISOString(), // Client-side timestamp for immediate UI use
        updated: new Date().toISOString()
      }

      // Add to local state
      campaigns.value.push(newCampaign)

      clearPublicCampaignCaches()
      return newCampaign
    } catch (err) {
      console.error('Error creating campaign:', err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function updateCampaign(campaignId, campaignData) {
    if (!campaignId) {
      throw new Error('Campaign ID is required')
    }

    isLoading.value = true
    error.value = null

    try {
      // Verify permission
      const userId = userStore.userId
      const campaignRef = doc(db, 'campaigns', campaignId)
      // getDoc must only be used inside async functions; ensure this line is not duplicated outside async
      const campaignDoc = await getDoc(campaignRef)

      if (!campaignDoc.exists()) {
        throw new Error('Campaign not found')
      }

      const campaign = campaignDoc.data()

      // Only the DM or an admin can update the campaign
      if (campaign.dm !== userId && !userStore.isAdmin) {
        throw new Error("You don't have permission to update this campaign")
      }

      // Prepare update data
      const updateData = {
        ...campaignData,
        updated: serverTimestamp()
      }

      // Remove fields that shouldn't be updated directly
      delete updateData.id
      delete updateData.created
      delete updateData.dm // Don't allow changing the DM

      await updateDoc(campaignRef, updateData)

      // Update local state
      const updatedCampaign = {
        id: campaignId,
        ...updateData,
        dm: campaign.dm, // Preserve the original DM
        updated: new Date().toISOString() // Client-side timestamp for immediate UI use
      }

      // Update in local array
      const index = campaigns.value.findIndex((c) => c.id === campaignId)
      if (index !== -1) {
        campaigns.value[index] = updatedCampaign
      }

      // Update current campaign if it's the active one
      if (currentCampaign.value && currentCampaign.value.id === campaignId) {
        currentCampaign.value = updatedCampaign
      }

      clearPublicCampaignCaches()
      return updatedCampaign
    } catch (err) {
      console.error(`Error updating campaign ${campaignId}:`, err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function deleteCampaign(campaignId) {
    if (!campaignId) {
      throw new Error('Campaign ID is required')
    }

    isLoading.value = true
    error.value = null

    try {
      // Verify permission
      const userId = userStore.userId
      const campaignRef = doc(db, 'campaigns', campaignId)
      const campaignDoc = await getDoc(campaignRef)

      if (!campaignDoc.exists()) {
        throw new Error('Campaign not found')
      }

      const campaign = campaignDoc.data()

      // Only the DM or an admin can delete the campaign
      if (campaign.dm !== userId && !userStore.isAdmin) {
        throw new Error("You don't have permission to delete this campaign")
      }

      // 1. Get campaign data for cleanup
      const playerEntries = normalizePlayers(campaign.players)
      const playerIds = Object.keys(playerEntries)
      const characters = campaign.characters || []
      const hasCoverPhoto = !!campaign.coverPhoto

      // 2. Update all characters associated with this campaign
      // Remove the campaign association from each character
      await Promise.all(
        characters.map(async (characterId) => {
          try {
            const characterRef = doc(db, 'characters', characterId)
            const characterDoc = await getDoc(characterRef)

            if (characterDoc.exists()) {
              await updateDoc(characterRef, {
                campaignId: null,
                updated: serverTimestamp()
              })
              console.log(`Removed campaign association from character ${characterId}`)
            }
          } catch (err) {
            console.error(`Error updating character ${characterId}:`, err)
            // Continue with other characters even if one fails
          }
        })
      )

      // 3. Clear user cache for all players
      playerIds.forEach((playerId) => {
        userStore.clearUserCache(playerId)
      })

      // 4. Delete campaign media (cover photo, etc.)
      if (hasCoverPhoto) {
        try {
          await MediaService.deleteCampaignCover(campaignId)
          console.log(`Deleted campaign cover photo for ${campaignId}`)
        } catch (err) {
          console.error(`Error deleting campaign cover photo:`, err)
          // Continue with deletion even if media cleanup fails
        }
      }

      // 5. Check for and delete any campaign resources
      // This would depend on your implementation of campaign resources
      try {
        // Query for campaign resources
        const resourcesQuery = query(
          collection(db, 'campaignResources'),
          where('campaignId', '==', campaignId)
        )
        const resourceDocs = await getDocs(resourcesQuery)

        // Delete each resource document
        const resourceDeletions = resourceDocs.docs.map(async (resourceDoc) => {
          await deleteDoc(resourceDoc.ref)
          console.log(`Deleted campaign resource ${resourceDoc.id}`)
        })

        await Promise.all(resourceDeletions)
      } catch (err) {
        console.error(`Error cleaning up campaign resources:`, err)
        // Continue with main deletion even if resource cleanup fails
      }

      // 6. Delete the campaign document itself
      await deleteDoc(campaignRef)
      console.log(`Campaign ${campaignId} successfully deleted`)

      // 7. Update local state
      campaigns.value = campaigns.value.filter((c) => c.id !== campaignId)

      // 8. Clear current campaign if it was the active one
      if (currentCampaign.value && currentCampaign.value.id === campaignId) {
        currentCampaign.value = null
      }

      clearPublicCampaignCaches()
      return true
    } catch (err) {
      console.error(`Error deleting campaign ${campaignId}:`, err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function fetchCampaignCharacters(campaignId) {
    if (!campaignId) {
      throw new Error('Campaign ID is required')
    }

    isLoading.value = true
    error.value = null

    try {
      if (typeof userStore.waitForAuthState === 'function') {
        await userStore.waitForAuthState({ requireUser: false, timeoutMs: 8000 })
      }

      let campaign = currentCampaign.value?.id === campaignId ? currentCampaign.value : null
      if (!campaign) {
        const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId))
        if (!campaignDoc.exists()) {
          throw new Error('Campaign not found')
        }
        campaign = campaignDoc.data()
      }
      // Use mapped object structure for characters
      const characterObj = campaign.characters || {}
      const characterIds = Array.isArray(characterObj)
        ? characterObj.filter(Boolean)
        : Object.keys(characterObj)

      if (characterIds.length === 0) {
        campaignCharacters.value = []
        return []
      }

      // Fetch all characters in the campaign
      const characters = await Promise.all(
        characterIds.map(async (id) => {
          try {
            const charDoc = await getDoc(doc(db, 'characters', id))
            if (charDoc.exists()) {
              return {
                id: charDoc.id,
                ...charDoc.data()
              }
            }
          } catch (characterError) {
            const code = String(characterError?.code || '')
            const message = String(characterError?.message || '').toLowerCase()
            const isPermissionError =
              code.includes('permission') || message.includes('permission')

            if (isPermissionError) {
              if (import.meta.env.DEV) {
                console.debug(
                  `[CampaignStore] Skipping inaccessible character ${id} in campaign ${campaignId}`
                )
              }
              return null
            }

            console.warn(
              `[CampaignStore] Failed to load character ${id} for campaign ${campaignId}:`,
              characterError
            )
          }

          return null
        })
      )

      // Filter out nulls (characters that couldn't be fetched)
      const validCharacters = characters.filter((char) => char !== null)
      campaignCharacters.value = validCharacters.map((it) => targetedCloneForMutation(it))

      // Opportunistically refresh preview docs for readable characters so older
      // campaign rosters backfill over time without a separate migration step.
      await Promise.allSettled(
        validCharacters.map((character) => {
          const characterId = String(character?.id || '').trim()
          if (!characterId) return Promise.resolve()
          return setDoc(
            getCampaignCharacterPreviewDocRef(campaignId, characterId),
            buildCampaignCharacterPreviewDoc(characterId, character),
            { merge: true }
          )
        })
      )

      return validCharacters
    } catch (err) {
      campaignCharacters.value = []
      console.error(`Error fetching characters for campaign ${campaignId}:`, err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function addCharacterToCampaign(campaignId, characterId) {
    if (!campaignId || !characterId) return

    const userId = userStore.userId
    if (!userId) {
      throw new Error('User authentication required')
    }

    isLoading.value = true
    error.value = null

    try {
      const campaignRef = doc(db, 'campaigns', campaignId)
      const campaignDoc = await getDoc(campaignRef)
      if (!campaignDoc.exists()) throw new Error('Campaign not found')
      const campaignData = campaignDoc.data()

      const isAdmin = userStore.isAdmin
      const isDM = campaignData.dm === userId
      const isPlayer = hasPlayer(campaignData.players, userId)

      if (!isAdmin && !isDM && !isPlayer) {
        throw new Error("You don't have permission to add characters to this campaign")
      }

      const characterRef = doc(db, 'characters', characterId)
      const characterDoc = await getDoc(characterRef)
      if (!characterDoc.exists()) {
        throw new Error('Character not found')
      }

      const characterData = characterDoc.data()
      const ownerId = characterData.owner || characterData.userId || characterData.createdBy || null
      const currentCampaignIds = resolveCharacterCampaignIds(characterData)
      const nextCampaignIds = Array.from(new Set([...currentCampaignIds, String(campaignId)]))

      if (!isAdmin && !isDM && ownerId !== userId) {
        throw new Error('You can only add characters you own to this campaign')
      }

      const updatedCharacters = toCharacterMap(campaignData.characters)

      if (Object.prototype.hasOwnProperty.call(updatedCharacters, characterId)) {
        return true
      }

      updatedCharacters[characterId] = {
        id: characterId,
        owner: ownerId,
        ...buildCharacterCampaignPreview(characterData),
        addedBy: userId,
        addedAt: new Date().toISOString()
      }

      const batch = writeBatch(db)
      batch.update(campaignRef, {
        characters: updatedCharacters,
        updated: serverTimestamp()
      })
      batch.update(characterRef, {
        // Track the most recently joined campaign for backwards compatibility,
        // while maintaining all linked campaigns for preview sync.
        campaignId,
        campaignIds: nextCampaignIds,
        updated: serverTimestamp()
      })
      batch.set(
        getCampaignCharacterPreviewDocRef(campaignId, characterId),
        buildCampaignCharacterPreviewDoc(characterId, {
          ...characterData,
          owner: ownerId,
          campaignId,
          campaignIds: nextCampaignIds
        }),
        { merge: true }
      )
      await batch.commit()

      // Update local state
      if (currentCampaign.value && currentCampaign.value.id === campaignId) {
        currentCampaign.value.characters = updatedCharacters
      }

      const campaignIndex = campaigns.value.findIndex((c) => c.id === campaignId)
      if (campaignIndex !== -1) {
        campaigns.value[campaignIndex].characters = updatedCharacters
      }

      const normalizedCharacter = targetedCloneForMutation({
        id: characterId,
        ...characterData,
        owner: ownerId,
        campaignId,
        campaignIds: nextCampaignIds
      })
      const withoutDuplicate = campaignCharacters.value.filter((c) => c.id !== characterId)
      campaignCharacters.value = [...withoutDuplicate, normalizedCharacter]
      clearPublicCampaignCaches()
    } catch (err) {
      console.error('Error adding character to campaign:', err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function removeCharacterFromCampaign(campaignId, characterId) {
    if (!campaignId || !characterId) return

    const userId = userStore.userId
    if (!userId) {
      throw new Error('User authentication required')
    }

    isLoading.value = true
    error.value = null

    try {
      const campaignRef = doc(db, 'campaigns', campaignId)
      const campaignDoc = await getDoc(campaignRef)
      if (!campaignDoc.exists()) throw new Error('Campaign not found')
      const campaignData = campaignDoc.data()

      const updatedCharacters = toCharacterMap(campaignData.characters)
      if (!Object.prototype.hasOwnProperty.call(updatedCharacters, characterId)) {
        throw new Error('Character not in campaign')
      }

      const entryMeta = updatedCharacters[characterId]
      const entryOwnerId =
        entryMeta?.owner || entryMeta?.ownerId || entryMeta?.userId || entryMeta?.createdBy || null

      const characterRef = doc(db, 'characters', characterId)
      let characterExists = false
      let characterData = null
      try {
        const characterDoc = await getDoc(characterRef)
        if (characterDoc.exists()) {
          characterExists = true
          characterData = characterDoc.data()
        }
      } catch (readError) {
        const code = String(readError?.code || '')
        const message = String(readError?.message || '').toLowerCase()
        const isPermissionError =
          code.includes('permission') || message.includes('permission')
        if (!isPermissionError) {
          throw readError
        }
      }

      const ownerId =
        characterData?.owner ||
        characterData?.userId ||
        characterData?.createdBy ||
        entryOwnerId ||
        null
      const remainingCampaignIds = characterExists
        ? resolveCharacterCampaignIds(characterData).filter((id) => id !== String(campaignId))
        : []
      const isAdmin = userStore.isAdmin
      const isDM = campaignData.dm === userId
      const userRole = getPlayerRole(campaignData.players, userId)
      const isModerator = userRole === 'moderator'
      const addedByUser = entryMeta?.addedBy === userId

      if (!isAdmin && !isDM && ownerId !== userId && !(isModerator && addedByUser)) {
        throw new Error("You don't have permission to remove this character")
      }

      delete updatedCharacters[characterId]

      const batch = writeBatch(db)
      batch.update(campaignRef, {
        characters: updatedCharacters,
        updated: serverTimestamp()
      })
      if (characterExists && characterData) {
        batch.update(characterRef, {
          // Only clear the primary campaignId if it was pointing at this campaign.
          campaignId:
            characterData.campaignId === campaignId
              ? remainingCampaignIds[0] || null
              : characterData.campaignId,
          campaignIds: remainingCampaignIds,
          updated: serverTimestamp()
        })
      }
      batch.delete(getCampaignCharacterPreviewDocRef(campaignId, characterId))
      await batch.commit()

      // Update local state
      if (currentCampaign.value && currentCampaign.value.id === campaignId) {
        currentCampaign.value.characters = updatedCharacters
      }

      const campaignIndex = campaigns.value.findIndex((c) => c.id === campaignId)
      if (campaignIndex !== -1) {
        campaigns.value[campaignIndex].characters = updatedCharacters
      }

      campaignCharacters.value = campaignCharacters.value.filter((c) => c.id !== characterId)
      clearPublicCampaignCaches()
    } catch (err) {
      console.error('Error removing character from campaign:', err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function addPlayerToCampaign(campaignId, playerId, role = 'player') {
    if (!campaignId || !playerId) {
      throw new Error('Campaign ID and Player ID are required')
    }

    isLoading.value = true
    error.value = null

    try {
      // Verify permission
      const userId = userStore.userId
      const campaignRef = doc(db, 'campaigns', campaignId)
      const campaignDoc = await getDoc(campaignRef)

      if (!campaignDoc.exists()) {
        throw new Error('Campaign not found')
      }

      const campaign = campaignDoc.data()

      // Only the DM or an admin can add players to the campaign
      if (campaign.dm !== userId && !userStore.isAdmin) {
        throw new Error("You don't have permission to add players to this campaign")
      }

      const playersObj = normalizePlayers(campaign.players)
      const existingEntry = playersObj[playerId]

      playersObj[playerId] = {
        role: role || DEFAULT_PLAYER_ROLE,
        joinedAt: existingEntry?.joinedAt || new Date().toISOString()
      }

      await updateDoc(campaignRef, {
        players: playersObj,
        updated: serverTimestamp()
      })

      // Update local state if current campaign is active
      if (currentCampaign.value && currentCampaign.value.id === campaignId) {
        currentCampaign.value.players = playersObj
      }

      // Update in campaigns array
      const index = campaigns.value.findIndex((c) => c.id === campaignId)
      if (index !== -1) {
        campaigns.value[index].players = playersObj
      }

      clearPublicCampaignCaches()
      return true
    } catch (err) {
      console.error(`Error adding player ${playerId} to campaign ${campaignId}:`, err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function removePlayerFromCampaign(campaignId, playerId) {
    try {
      // Validate required parameters
      if (!campaignId) throw new Error('Campaign ID is required')
      if (!playerId) throw new Error('Player ID is required')

      isLoading.value = true

      // Get campaign reference
      const campaignRef = doc(db, 'campaigns', campaignId)
      // Get current campaign data
      const campaignDoc = await getDoc(campaignRef)
      if (!campaignDoc.exists()) throw new Error('Campaign not found')
      const campaignData = campaignDoc.data()

      const updatedPlayers = normalizePlayers(campaignData.players)
      if (!Object.prototype.hasOwnProperty.call(updatedPlayers, playerId)) {
        throw new Error('Player not in campaign')
      }
      delete updatedPlayers[playerId]

      // Update the campaign document
      await updateDoc(campaignRef, {
        players: updatedPlayers,
        updated: serverTimestamp()
      })

      // Clear user cache to ensure fresh data on next fetch
      userStore.clearUserCache(playerId)

      // Update local state to avoid full reload
      if (currentCampaign.value && currentCampaign.value.id === campaignId) {
        currentCampaign.value.players = updatedPlayers
      }

      clearPublicCampaignCaches()
      return true
    } catch (error) {
      console.error('Error removing player from campaign:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }
  // Update a player's role in a campaign
  async function updatePlayerRoleInCampaign(campaignId, playerId, newRole) {
    if (!campaignId || !playerId || !newRole) {
      throw new Error('Campaign ID, Player ID, and new role are required')
    }

    isLoading.value = true
    error.value = null

    try {
      const userId = userStore.userId
      const campaignRef = doc(db, 'campaigns', campaignId)
      const campaignDoc = await getDoc(campaignRef)

      if (!campaignDoc.exists()) {
        throw new Error('Campaign not found')
      }

      const campaign = campaignDoc.data()

      // Only the DM or an admin can update player roles
      if (campaign.dm !== userId && !userStore.isAdmin) {
        throw new Error("You don't have permission to update player roles in this campaign")
      }

      const playersObj = normalizePlayers(campaign.players)

      if (!playersObj[playerId]) {
        throw new Error('Player not found in campaign')
      }

      playersObj[playerId].role = newRole

      await updateDoc(campaignRef, {
        players: playersObj,
        updated: serverTimestamp()
      })

      // Update local state if current campaign is active
      if (currentCampaign.value && currentCampaign.value.id === campaignId) {
        currentCampaign.value.players = playersObj
      }

      // Update in campaigns array
      const index = campaigns.value.findIndex((c) => c.id === campaignId)
      if (index !== -1) {
        campaigns.value[index].players = playersObj
      }

      clearPublicCampaignCaches()
      return true
    } catch (err) {
      console.error(`Error updating player role for ${playerId} in campaign ${campaignId}:`, err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }
  // New methods for handling campaign photos
  async function uploadCampaignPhoto(campaignId, file) {
    if (!campaignId || !file) {
      throw new Error('Campaign ID and file are required')
    }

    isLoading.value = true
    error.value = null

    try {
      // Verify permission
      const userId = userStore.userId
      const campaignRef = doc(db, 'campaigns', campaignId)
      const campaignDoc = await getDoc(campaignRef)

      if (!campaignDoc.exists()) {
        throw new Error('Campaign not found')
      }

      const campaign = campaignDoc.data()

      // Only the DM or an admin can upload photos
      if (campaign.dm !== userId && !userStore.isAdmin) {
        throw new Error("You don't have permission to upload photos to this campaign")
      }

      const ext = (file.name?.split('.').pop() || file.type.split('/')[1] || 'png').toLowerCase()
      const storagePath = `campaigns/${campaignId}/cover/${uuidv4()}.${ext}`
      const res = await MediaService.uploadManaged({
        storagePath,
        file,
        userId,
        resourceType: 'campaign-cover',
        makePublic: true,
        deleteAfterSuccess: { folderExcept: { folderPath: `campaigns/${campaignId}/cover` } }
      })
      const photoURL = res?.url || null

      // Update campaign with photo URL
      await updateDoc(campaignRef, {
        coverPhoto: photoURL,
        updated: serverTimestamp()
      })

      // Update local state
      if (currentCampaign.value && currentCampaign.value.id === campaignId) {
        currentCampaign.value = {
          ...currentCampaign.value,
          coverPhoto: photoURL
        }
      }

      // Update in campaigns array
      const index = campaigns.value.findIndex((c) => c.id === campaignId)
      if (index !== -1) {
        campaigns.value[index] = {
          ...campaigns.value[index],
          coverPhoto: photoURL
        }
      }

      clearPublicCampaignCaches()
      return photoURL
    } catch (err) {
      console.error(`Error uploading campaign photo for ${campaignId}:`, err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function deleteCampaignPhoto(campaignId) {
    if (!campaignId) {
      throw new Error('Campaign ID is required')
    }

    isLoading.value = true
    error.value = null

    try {
      // Verify permission
      const userId = userStore.userId
      const campaignRef = doc(db, 'campaigns', campaignId)
      const campaignDoc = await getDoc(campaignRef)

      if (!campaignDoc.exists()) {
        throw new Error('Campaign not found')
      }

      const campaign = campaignDoc.data()

      // Only the DM or an admin can delete photos
      if (campaign.dm !== userId && !userStore.isAdmin) {
        throw new Error("You don't have permission to delete photos from this campaign")
      }

      // Delete the photo using MediaService
      await MediaService.deleteCampaignCover(campaignId)

      // Update campaign to remove photo URL
      await updateDoc(campaignRef, {
        coverPhoto: null,
        updated: serverTimestamp()
      })

      // Update local state
      if (currentCampaign.value && currentCampaign.value.id === campaignId) {
        currentCampaign.value = {
          ...currentCampaign.value,
          coverPhoto: null
        }
      }

      // Update in campaigns array
      const index = campaigns.value.findIndex((c) => c.id === campaignId)
      if (index !== -1) {
        campaigns.value[index] = {
          ...campaigns.value[index],
          coverPhoto: null
        }
      }

      clearPublicCampaignCaches()
      return true
    } catch (err) {
      console.error(`Error deleting campaign photo for ${campaignId}:`, err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Refresh campaigns for listing page (used after player operations)
  async function refreshCampaignsListing() {
    // This function explicitly refreshes campaigns list without showing loading state
    try {
      const userId = userStore.userId

      if (!userId) return false

      console.log('Refreshing campaigns listing...')

      const campaignsRef = collection(db, 'campaigns')
      const dmQuery = query(campaignsRef, where('dm', '==', userId))
      const roleFieldPath = new FieldPath('players', userId, 'role')
      const playerQuery = query(campaignsRef, where(roleFieldPath, 'in', ['player', 'moderator']))

      const [dmSnapshot, playerSnapshot] = await Promise.all([
        getDocs(dmQuery),
        getDocs(playerQuery)
      ])

      const mapCampaign = (docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          ...data,
          players: normalizePlayers(data.players)
        }
      }

      const campaignMap = new Map()
      dmSnapshot.docs.forEach((docSnap) => {
        const campaign = mapCampaign(docSnap)
        campaignMap.set(campaign.id, campaign)
      })
      playerSnapshot.docs.forEach((docSnap) => {
        const campaign = mapCampaign(docSnap)
        if (!campaignMap.has(campaign.id)) {
          campaignMap.set(campaign.id, campaign)
        }
      })

      campaigns.value = Array.from(campaignMap.values()).map((campaign) =>
        targetedCloneForMutation(campaign)
      )

      console.log('Campaigns listing refreshed successfully')
      return true
    } catch (error) {
      console.error('Error refreshing campaigns listing:', error)
      return false
    }
  }

  // Add a clear method for the current campaign
  function clearCurrentCampaign() {
    currentCampaign.value = null
    // Also clean up any active subscription when clearing
    unsubscribeFromCampaign()
  }

  const getCachedPublicCampaigns = (options = {}) => {
    const entry = readPublicCampaignListCacheEntry(options)
    return entry ? entry.data : null
  }

  const getCachedPaginatedPublicCampaigns = (options = {}) => {
    const cacheKey = getPublicCampaignPageCacheKey(options)
    const entry = readPublicCampaignPageCacheEntry(cacheKey, options)
    return entry ? entry.data : null
  }

  const clearPublicCampaignCaches = () => {
    clearPublicCampaignCachesInternal()
  }

  const mapPublicCampaignSnapshot = (docSnap) => {
    const data = docSnap.data()
    return normalizePublicCampaignRecord({
      id: docSnap.id,
      ...data
    })
  }

  const resolvePublicCampaignCursor = async (lastDoc) => {
    if (!lastDoc) return null
    if (typeof lastDoc.data === 'function') return lastDoc
    if (!lastDoc.id) return null

    try {
      const cursorSnap = await getDoc(doc(db, 'campaigns', lastDoc.id))
      return cursorSnap.exists() ? cursorSnap : null
    } catch {
      return null
    }
  }

  const fetchPublicCampaignsFromFirestore = async () => {
    const publicCampaignsQuery = query(
      collection(db, 'campaigns'),
      where('settings.isPublic', '==', true)
    )

    const querySnapshot = await getDocs(publicCampaignsQuery)
    return querySnapshot.docs.map((docSnap) => mapPublicCampaignSnapshot(docSnap))
  }

  /**
   * Fetch public campaigns that users can join
   * @returns {Promise<Array>} - Array of public campaigns
   */
  async function fetchPublicCampaigns(options = {}) {
    const { forceRefresh = false, maxAgeMs = PUBLIC_CAMPAIGNS_CACHE_TTL_MS } = options

    isLoading.value = true
    error.value = null

    try {
      if (!forceRefresh) {
        const cached = getCachedPublicCampaigns({ maxAgeMs })
        if (cached) return cached
      }

      if (publicCampaignsListPendingRequest) {
        return await publicCampaignsListPendingRequest
      }

      publicCampaignsListPendingRequest = (async () => {
        try {
          const freshCampaigns = await fetchPublicCampaignsFromFirestore()
          return writePublicCampaignListCache(freshCampaigns)
        } finally {
          publicCampaignsListPendingRequest = null
        }
      })()

      return await publicCampaignsListPendingRequest
    } catch (err) {
      console.error('Error fetching public campaigns:', err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Fetch paginated public campaigns
   * @param {Object} options - Pagination options
   * @param {number} options.pageSize - Number of campaigns per page
   * @param {string} options.sortBy - Field to sort by (default: 'created')
   * @param {boolean} options.descending - Sort order (default: true)
   * @param {Object} options.lastDoc - Last document from previous page (for pagination)
   * @returns {Promise<Object>} - { campaigns, lastDoc, total }
   */
  async function fetchPaginatedPublicCampaigns(options = {}) {
    const {
      pageSize = 25,
      sortBy = 'created',
      descending = true,
      lastDoc = null,
      page = 1,
      forceRefresh = false,
      maxAgeMs = PUBLIC_CAMPAIGNS_CACHE_TTL_MS
    } = options

    const cacheKey = getPublicCampaignPageCacheKey({ page, pageSize, sortBy, descending })

    isLoading.value = true
    error.value = null

    try {
      if (!forceRefresh) {
        const cached = getCachedPaginatedPublicCampaigns({
          page,
          pageSize,
          sortBy,
          descending,
          maxAgeMs
        })
        if (cached) return cached
      }

      console.log('[CampaignStore] Fetching paginated public campaigns:', {
        pageSize,
        sortBy,
        descending,
        hasLastDoc: !!lastDoc
      })

      // First, let's check if there are ANY public campaigns
      const debugQuery = query(collection(db, 'campaigns'), where('settings.isPublic', '==', true))
      const debugSnapshot = await getDocs(debugQuery)
      console.log(
        '[CampaignStore] DEBUG - Total public campaigns (no orderBy):',
        debugSnapshot.docs.length
      )
      if (debugSnapshot.docs.length > 0) {
        const firstDoc = debugSnapshot.docs[0].data()
        console.log('[CampaignStore] DEBUG - First campaign:', {
          id: debugSnapshot.docs[0].id,
          name: firstDoc.name,
          settings: firstDoc.settings,
          createdAt: firstDoc.createdAt,
          hasCreatedAt: !!firstDoc.createdAt
        })

        // Check if the sortBy field exists on documents
        if (!firstDoc[sortBy]) {
          console.warn(
            '[CampaignStore] ⚠️  Sort field "' + sortBy + '" does not exist on campaign documents!'
          )
          console.warn('[CampaignStore] Available fields:', Object.keys(firstDoc))
          console.warn('[CampaignStore] Falling back to simple query without sorting')

          // Use fallback without orderBy since the field doesn't exist
          throw new Error('MISSING_SORT_FIELD')
        }
      }

      // Try with pagination and sorting (requires index)
      try {
        // Build base query for public campaigns
        // Note: Using orderBy with where requires a composite index in Firestore
        const resolvedLastDoc = await resolvePublicCampaignCursor(lastDoc)
        let q
        if (resolvedLastDoc) {
          q = query(
            collection(db, 'campaigns'),
            where('settings.isPublic', '==', true),
            orderBy(sortBy, descending ? 'desc' : 'asc'),
            startAfter(resolvedLastDoc),
            limit(pageSize)
          )
        } else {
          q = query(
            collection(db, 'campaigns'),
            where('settings.isPublic', '==', true),
            orderBy(sortBy, descending ? 'desc' : 'asc'),
            limit(pageSize)
          )
        }

        console.log('[CampaignStore] Executing query with orderBy...')
        const querySnapshot = await getDocs(q)
        console.log('[CampaignStore] Query returned:', querySnapshot.docs.length, 'campaigns')
        console.log('[CampaignStore] Query snapshot empty?', querySnapshot.empty)

        // Get total count of public campaigns (for pagination info)
        const countQuery = query(
          collection(db, 'campaigns'),
          where('settings.isPublic', '==', true)
        )
        const countSnapshot = await getCountFromServer(countQuery)
        const total = countSnapshot.data().count
        console.log('[CampaignStore] Total public campaigns:', total)

        // Convert query results to usable array and clone per-item
        const campaigns = querySnapshot.docs.map((docSnap) => mapPublicCampaignSnapshot(docSnap))

        // Get the last document for next page pagination
        const lastDocument = querySnapshot.docs[querySnapshot.docs.length - 1] || null

        return writePublicCampaignPageCache(cacheKey, {
          campaigns,
          lastDoc: lastDocument,
          total
        })
      } catch (indexError) {
        // If it's an index error or missing field, fall back to simpler query without orderBy
        if (
          indexError.code === 'failed-precondition' ||
          indexError.message?.includes('index') ||
          indexError.message === 'MISSING_SORT_FIELD'
        ) {
          if (indexError.message === 'MISSING_SORT_FIELD') {
            console.warn(
              '[CampaignStore] ⚠️  Sort field does not exist on documents, using fallback query'
            )
          } else {
            console.warn(
              '[CampaignStore] ⚠️  Composite index not available, falling back to simple query'
            )
            console.warn(
              '[CampaignStore] To enable sorting, create index: campaigns collection, fields: settings.isPublic (==), ' +
                sortBy +
                ' (' +
                (descending ? 'desc' : 'asc') +
                ')'
            )
            console.warn(
              '[CampaignStore] Index creation link should be in the error above or check Firebase Console > Firestore > Indexes'
            )
          }

          // Fallback: fetch all public campaigns without orderBy, then sort client-side
          const simpleQuery = query(
            collection(db, 'campaigns'),
            where('settings.isPublic', '==', true)
          )

          const querySnapshot = await getDocs(simpleQuery)
          console.log(
            '[CampaignStore] Fallback query returned:',
            querySnapshot.docs.length,
            'campaigns'
          )

          // Convert to array and clone per-item
          let allCampaigns = querySnapshot.docs.map((docSnap) => mapPublicCampaignSnapshot(docSnap))

          // Sort client-side if the field exists
          if (allCampaigns.length > 0 && allCampaigns[0][sortBy] !== undefined) {
            allCampaigns.sort((a, b) => {
              const aVal = a[sortBy]
              const bVal = b[sortBy]
              if (descending) {
                return bVal > aVal ? 1 : bVal < aVal ? -1 : 0
              } else {
                return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
              }
            })
          } else {
            // Sort by name as fallback
            console.log('[CampaignStore] Sorting by name as fallback')
            allCampaigns.sort((a, b) => {
              const aName = (a.name || '').toLowerCase()
              const bName = (b.name || '').toLowerCase()
              return descending ? bName.localeCompare(aName) : aName.localeCompare(bName)
            })
          }

          // Manually paginate
          const startIndex = lastDoc ? allCampaigns.findIndex((c) => c.id === lastDoc.id) + 1 : 0
          const campaigns = allCampaigns.slice(startIndex, startIndex + pageSize)

          // Create a mock lastDoc for next page
          const lastCampaign = campaigns[campaigns.length - 1]
          const lastDocument = lastCampaign ? { id: lastCampaign.id } : null

          return writePublicCampaignPageCache(cacheKey, {
            campaigns,
            lastDoc: lastDocument,
            total: allCampaigns.length
          })
        } else {
          // Different error, re-throw
          throw indexError
        }
      }
    } catch (err) {
      console.error('[CampaignStore] Error fetching paginated public campaigns:', err)
      console.error('[CampaignStore] Error details:', err.code, err.message)

      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Request to join a campaign
   * @param {string} campaignId - Campaign ID to join
   * @returns {Promise<boolean>} - Success status
   */
  async function requestToJoinCampaign(campaignId) {
    if (!campaignId) {
      throw new Error('Campaign ID is required')
    }

    if (!userStore.isAuthenticated) {
      throw new Error('You must be logged in to join a campaign')
    }

    isLoading.value = true
    error.value = null

    try {
      const userId = userStore.userId
      const campaignRef = doc(db, 'campaigns', campaignId)
      const campaignDoc = await getDoc(campaignRef)

      if (!campaignDoc.exists()) {
        throw new Error('Campaign not found')
      }

      const campaign = campaignDoc.data()

      if (!campaign.settings?.allowPlayerJoin) {
        throw new Error('This campaign is not accepting join requests')
      }

      const normalizedPlayers = normalizePlayers(campaign.players)
      if (normalizedPlayers[userId]) {
        throw new Error('You are already a member of this campaign')
      }

      const limitEnabled = campaign.settings?.limitPlayerSlots === true
      const maxSlots = Number(campaign.settings?.maxPlayerSlots) || 0
      if (limitEnabled && maxSlots > 0 && getPlayerCount(normalizedPlayers) >= maxSlots) {
        throw new Error('This campaign has reached its maximum player capacity')
      }

      const updatedPlayers = {
        ...normalizedPlayers,
        [userId]: {
          role: DEFAULT_PLAYER_ROLE,
          joinedAt: new Date().toISOString(),
          joinedVia: 'request'
        }
      }

      await updateDoc(campaignRef, {
        players: updatedPlayers,
        updated: serverTimestamp()
      })

      if (currentCampaign.value && currentCampaign.value.id === campaignId) {
        currentCampaign.value = targetedCloneForMutation({
          ...currentCampaign.value,
          players: updatedPlayers
        })
      }

      const index = campaigns.value.findIndex((c) => c.id === campaignId)
      if (index !== -1) {
        campaigns.value[index] = targetedCloneForMutation({
          ...campaigns.value[index],
          players: updatedPlayers
        })
      }

      userStore.clearUserCache(userId)

      clearPublicCampaignCaches()
      return true
    } catch (err) {
      console.error(`Error joining campaign ${campaignId}:`, err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function joinCampaignByInviteCode(inviteCode, userIdOverride = null) {
    const trimmedCode = inviteCode?.trim()
    if (!trimmedCode) {
      throw new Error('Invite code is required')
    }

    const userId = userIdOverride || userStore.userId
    if (!userId) {
      throw new Error('You must be logged in to join a campaign')
    }

    const campaignsRef = collection(db, 'campaigns')
    const inviteQuery = query(campaignsRef, where('inviteCode', '!=', null))
    const snapshot = await getDocs(inviteQuery)

    const normalizedInput = trimmedCode.toUpperCase()
    const docSnap = snapshot.docs.find((doc) => {
      const code = (doc.data().inviteCode || '').trim().toUpperCase()
      return code === normalizedInput
    })

    if (!docSnap) {
      throw new Error('No campaign found with that invite code.')
    }

    const campaignData = docSnap.data()
    if (!campaignData.settings?.allowPlayerJoin) {
      throw new Error('This campaign does not allow players to join directly.')
    }

    const normalizedPlayers = normalizePlayers(campaignData.players)
    if (normalizedPlayers[userId]) {
      throw new Error('You are already a member of this campaign.')
    }

    const limitEnabled = campaignData.settings?.limitPlayerSlots === true
    const maxSlots = Number(campaignData.settings?.maxPlayerSlots) || 0
    if (limitEnabled && maxSlots > 0 && getPlayerCount(normalizedPlayers) >= maxSlots) {
      throw new Error('This campaign is full.')
    }

    const updatedPlayers = {
      ...normalizedPlayers,
      [userId]: {
        role: DEFAULT_PLAYER_ROLE,
        joinedAt: new Date().toISOString(),
        joinedVia: 'invite'
      }
    }

    const campaignId = docSnap.id
    await updateDoc(doc(db, 'campaigns', campaignId), {
      players: updatedPlayers,
      updated: serverTimestamp()
    })

    if (currentCampaign.value && currentCampaign.value.id === campaignId) {
      currentCampaign.value = targetedCloneForMutation({
        ...currentCampaign.value,
        players: updatedPlayers
      })
    }

    const index = campaigns.value.findIndex((c) => c.id === campaignId)
    if (index !== -1) {
      campaigns.value[index] = targetedCloneForMutation({
        ...campaigns.value[index],
        players: updatedPlayers
      })
    }

    clearPublicCampaignCaches()
    return campaignId
  }

  function subscribeToCampaign(campaignId) {
    if (!campaignId) {
      return
    }

    // Clear any existing subscription first
    if (campaignUnsubscribe.value) {
      try {
        campaignUnsubscribe.value()
      } catch (e) {
        console.error('Error unsubscribing from previous campaign snapshot:', e)
      }
      campaignUnsubscribe.value = null
    }

    const campaignRef = doc(db, 'campaigns', campaignId)

    isLoading.value = true
    error.value = null

    campaignUnsubscribe.value = onSnapshot(
      campaignRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          console.warn(`Campaign ${campaignId} no longer exists`)
          currentCampaign.value = null
          isLoading.value = false
          return
        }

        const data = snapshot.data()
        const normalized = {
          id: snapshot.id,
          ...data,
          players: normalizePlayers(data.players)
        }

        currentCampaign.value = targetedCloneForMutation(normalized)

        // Keep campaigns list in sync when possible
        const index = campaigns.value.findIndex((c) => c.id === snapshot.id)
        if (index !== -1) {
          campaigns.value[index] = targetedCloneForMutation({
            ...campaigns.value[index],
            ...normalized
          })
        }

        isLoading.value = false
      },
      (err) => {
        console.error('Error in campaign snapshot subscription:', err)
        error.value = err.message
        isLoading.value = false
      }
    )
  }

  function unsubscribeFromCampaign() {
    if (campaignUnsubscribe.value) {
      try {
        campaignUnsubscribe.value()
      } catch (e) {
        console.error('Error unsubscribing from campaign snapshot:', e)
      }
      campaignUnsubscribe.value = null
    }
  }

  return {
    // State
    campaigns,
    isLoading,
    error,
    currentCampaign,
    campaignCharacters,

    // Getters
    getCampaigns,
    getUserCampaigns,
    getDMCampaigns,
    getPlayerCampaigns,
    getCurrentCampaign,
    getCampaignCharacters,

    // Actions
    fetchCampaigns,
    fetchDMCampaigns,
    fetchPlayerCampaigns,
    fetchCampaign,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    fetchCampaignCharacters,
    addCharacterToCampaign,
    removeCharacterFromCampaign,
    addPlayerToCampaign,
    removePlayerFromCampaign,

    // Photo-related actions
    updatePlayerRoleInCampaign,
    uploadCampaignPhoto,
    deleteCampaignPhoto,

    // New actions
    clearCurrentCampaign,
    refreshCampaignsListing,
    clearPublicCampaignCaches,

    // Public campaign actions
    getCachedPublicCampaigns,
    getCachedPaginatedPublicCampaigns,
    fetchPublicCampaigns,
    fetchPaginatedPublicCampaigns,
    requestToJoinCampaign,

    // Expose join by invite code
    joinCampaignByInviteCode,

    // Live updates
    subscribeToCampaign,
    unsubscribeFromCampaign
  }
})
